import { FilterList } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  Menu,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { useState } from "react";
import { ApiError, ApiErrorClass, Exercise } from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { EditIcon, TrashIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import RFDataGrid from "src/grading/components/RFDataGrid/RFDataGrid";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { getRelativeTime } from "src/grading/utils";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  useDeleteExerciseLibraryMutation,
  useGetExerciseLibraryQuery,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import ExerciseModal from "./ExerciseModal/ExerciseModal";
import useStyles from "./Exercises.style";

const Exercises = () => {
  const classes = useStyles();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [limit, _setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(0);
  const { data: exercises, isFetching } = useGetExerciseLibraryQuery({
    limit,
    offset,
  });
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(
    null,
  );
  const [openExerciseModal, setOpenExerciseModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteMenuAnchorEl, setDeleteMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const deleteMenuOpen = Boolean(deleteMenuAnchorEl);
  const [deleteExerciseLibrary, { isLoading: isDeletingExercise }] =
    useDeleteExerciseLibraryMutation();
  const dispatch = useAppDispatch();

  const [_total, _setTotal] = useState(0);

  const handleEditExercise = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setOpenExerciseModal(true);
    setIsEditing(true);
  };

  const handleDeleteMenuClose = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setSelectedExercise(null);
    setDeleteMenuAnchorEl(null);
  };

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (!selectedExercise?.id) {
        throw new ApiErrorClass({
          message: "Exercise ID is required",
        });
      }
      await deleteExerciseLibrary({ id: selectedExercise?.id }).unwrap();
      dispatch(
        showSnackbar({
          open: true,
          message: "Exercise deleted successfully",
        }),
      );
      handleDeleteMenuClose(event);
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  return (
    <AthletesLayout>
      <Grid container className={classes.container}>
        {/* ATHLETES HEADER CONTAINER */}
        <Grid container item alignItems={"center"}>
          <Grid item>
            <Grid container alignItems={"center"}>
              <Typography variant="h2">My Library | Exercises</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* FILTERS */}
        <Grid
          container
          alignItems={"center"}
          justifyContent={"flex-end"}
          columnGap={2}
        >
          <Grid item>
            <Button variant="outlined" className={classes.sortButton}>
              <Typography variant="button" fontSize={"14px"}>
                Sort
              </Typography>
              <FilterList />
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              className={classes.addNewButton}
              onClick={() => setOpenExerciseModal(true)}
            >
              <Typography variant="button" fontSize={"14px"}>
                Add New
              </Typography>
            </Button>
          </Grid>
        </Grid>

        {/* DATA GRID */}
        <RFDataGrid
          rows={exercises?.data?.exercises || []}
          columns={[
            {
              field: "name",
              headerName: "Exercise Name",
              flex: 2,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 300,
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Exercise Name
                </Typography>
              ),
              renderCell: ({ row }) => {
                const name = row.name;
                return (
                  <Tooltip title={name}>
                    <Typography
                      variant="caption"
                      fontSize={"24px"}
                      color="black"
                      fontWeight={"bold"}
                    >
                      {row.name}
                    </Typography>
                  </Tooltip>
                );
              },
            },
            {
              field: "description",
              headerName: "Description",
              flex: 1,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 120,
              maxWidth: 150,
              align: "center",
              headerAlign: "center",
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Description
                </Typography>
              ),
              renderCell: ({ row }) => (
                <Tooltip title={row.description}>
                  <Typography
                    variant="caption"
                    fontSize={"18px"}
                    color="black"
                    fontWeight={"bold"}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {row.description}
                  </Typography>
                </Tooltip>
              ),
            },
            {
              field: "updatedAt",
              headerName: "Last update",
              flex: 0.7,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 150,
              maxWidth: 180,
              align: "center",
              headerAlign: "center",
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Last update
                </Typography>
              ),
              renderCell: ({ row }) => {
                const relativeTime = getRelativeTime(
                  new Date(row.updatedAt).getTime(),
                );
                return (
                  <Tooltip title={relativeTime}>
                    <Typography
                      variant="caption"
                      fontSize={"18px"}
                      color="black"
                      fontWeight={"bold"}
                    >
                      {relativeTime}
                    </Typography>
                  </Tooltip>
                );
              },
            },
            {
              field: "",
              flex: 0.4,
              editable: false,
              sortable: false,
              filterable: false,
              minWidth: 100,
              maxWidth: 120,
              renderCell: ({ row }) => (
                <Grid
                  container
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEditExercise(row)}>
                      <EditIcon stroke="black" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={(event) => {
                        setDeleteMenuAnchorEl(event.currentTarget);
                        setSelectedExercise(row);
                      }}
                    >
                      <TrashIcon stroke="black" />
                      <Menu
                        id="weekly-menu"
                        anchorEl={deleteMenuAnchorEl}
                        open={deleteMenuOpen}
                        onClose={handleDeleteMenuClose}
                        MenuListProps={{
                          "aria-labelledby": "weekly-button",
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: theme.spacing(1),
                              padding: theme.spacing(4),
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="h5"
                          fontSize={18}
                          fontWeight={"bold"}
                        >
                          Delete Exercise
                        </Typography>

                        <Typography
                          variant="body1"
                          fontSize={16}
                          marginTop={theme.spacing(1)}
                        >
                          Are you sure you want to delete this exercise?
                        </Typography>

                        <Grid
                          container
                          item
                          xs={12}
                          justifyContent="space-between"
                          marginTop={theme.spacing(2)}
                        >
                          <Grid item>
                            <Button
                              color="secondary"
                              className={` ${classes.button} ${classes.deleteButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMenuClose(e);
                              }}
                            >
                              Cancel
                            </Button>
                          </Grid>

                          <Grid item>
                            <LoadingButton
                              variant="contained"
                              color="primary"
                              className={classes.button}
                              label={"Delete Exercise"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(e);
                              }}
                              loading={isDeletingExercise}
                            />
                          </Grid>
                        </Grid>
                      </Menu>
                    </IconButton>
                  </Tooltip>
                </Grid>
              ),
            },
          ]}
          loading={isFetching}
          disableSelectionOnClick
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setSortModel(newSortModel);
          }}
          page={page}
          // rowCount={exercises?.data?.total || 0}
          pageSize={limit}
          onPageChange={(newPage) => {
            setPage(newPage);
            setOffset(newPage * limit);
          }}
          rowsPerPageOptions={[]}
        />
      </Grid>

      <ExerciseModal
        open={openExerciseModal}
        onClose={() => {
          setOpenExerciseModal(false);
          setIsEditing(false);
          setSelectedExercise(null);
        }}
        isEditing={isEditing}
        exercise={selectedExercise}
      />
    </AthletesLayout>
  );
};

export default Exercises;
